import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSaveForm } from '@/shared/lib/useSaveForm';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import { createWork, editWork, type Work, type WorkFormValues } from './api';

const ACTIVE_OPTIONS = [
  { value: '0', label: 'нет' },
  { value: '1', label: 'да' },
];

type TextField = 'name' | 'title' | 'alias' | 'icon';

const TEXT_FIELDS: { field: TextField; label: string; placeholder: string }[] = [
  { field: 'name', label: 'Название', placeholder: 'Название...' },
  { field: 'title', label: 'Title', placeholder: 'Title...' },
  { field: 'alias', label: 'Alias', placeholder: 'Alias...' },
  { field: 'icon', label: 'Иконка', placeholder: 'Иконка...' },
];

type Props = {
  /** Edit this work; when omitted a new one is created under `parentId`. */
  work?: Work;
  /** 0 creates a section. */
  parentId?: number;
  onClose: () => void;
};

export const WorkModal = ({ work, parentId = 0, onClose }: Props) => {
  const qc = useQueryClient();
  const [values, setValues] = useState({
    name: work?.name ?? '',
    title: work?.title ?? '',
    alias: work?.alias ?? '',
    icon: work?.icon ?? '',
  });
  const [isActive, setIsActive] = useState(String(work?.is_active ?? 0));

  const { errors, clearErrors, save, isPending } = useSaveForm(
    (form: WorkFormValues) => (work ? editWork(work.id, form) : createWork(parentId, form)),
    () => {
      void qc.invalidateQueries({ queryKey: ['works'] });
      onClose();
    },
  );

  return (
    <ModalDialog
      onClose={onClose}
      isSaving={isPending}
      onSave={() => save({ ...values, isActive: Number(isActive) })}
    >
      {TEXT_FIELDS.map(({ field, label, placeholder }) => (
        <FormField key={field} label={label} errors={errors[field]}>
          <input
            className="form-control input-sm"
            style={{ width: 250 }}
            placeholder={placeholder}
            value={values[field]}
            onChange={(e) => {
              setValues((prev) => ({ ...prev, [field]: e.target.value }));
              clearErrors(field);
            }}
          />
        </FormField>
      ))}
      <FormField label="Активировано для автосервисов" errors={errors.is_active}>
        <FilterSelect
          options={ACTIVE_OPTIONS}
          value={isActive}
          isClearable={false}
          onChange={(value) => {
            setIsActive(value);
            clearErrors('is_active');
          }}
        />
      </FormField>
      {errors.work_type_id && <p className="text-red">{errors.work_type_id[0]}</p>}
      {errors.common && <p className="text-red">{errors.common[0]}</p>}
    </ModalDialog>
  );
};
